/**
 * useEditableContent Hook
 * Generic hook for managing content with the unified ContentItem API
 * Handles all CRUD operations and modal state management
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

/**
 * @param {string} contentType - Type of content (skill, job, portfolio, etc.)
 * @param {object} queryParams - Additional query parameters for filtering
 * @returns {object} - Content items, loading state, and CRUD functions
 */
export const useEditableContent = (contentType, queryParams = {}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true); // Start as true since we fetch on mount
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { showToast } = useToast();

  // Stabilize queryParams to prevent infinite loops
  const stableQueryParams = useMemo(
    () => queryParams,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(queryParams)]
  );

  /**
   * Fetch items from API
   */
  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ 
        contentType, 
        ...stableQueryParams 
      });
      const response = await api.get(`/content-items?${params}`);
      setItems(response.data.data || []);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      
      // Handle 502 Bad Gateway specifically (server down/not reachable)
      let errorMessage;
      if (error.response?.status === 502) {
        errorMessage = 'Server is temporarily unavailable. Please try again in a moment.';
      } else if (error.response?.status === 503) {
        errorMessage = 'Service is temporarily unavailable. Please try again later.';
      } else if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        errorMessage = 'Unable to connect to server. Please check your connection.';
      } else {
        errorMessage = error.response?.data?.message || 
                      error.response?.data?.error || 
                      error.message || 
                      'Failed to fetch content';
      }
      
      setError(errorMessage);
      setItems([]);
      
      // Show error toast for server errors (502, 503) but not for network issues
      // Network errors are usually temporary and don't need user notification
      if (error.response?.status === 502 || error.response?.status === 503) {
        showToast('Server is temporarily unavailable', 'error');
      } else if (error.code !== 'ERR_NETWORK' && 
                 error.code !== 'ECONNREFUSED' && 
                 !error.message?.includes('Network Error') &&
                 !errorMessage.includes('timeout')) {
        // Only show toast if it's not a network-related error
        showToast(`Failed to load ${contentType} content: ${errorMessage}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [contentType, stableQueryParams, showToast]);

  /**
   * Auto-fetch on mount and when dependencies change
   */
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  /**
   * Start creating new item
   */
  const startCreate = useCallback((initialData = {}) => {
    setIsCreating(true);
    setEditingItem(null);
    setFormData({ 
      contentType, 
      metadata: { published: true, order: 0 },
      ...initialData 
    });
    setShowModal(true);
  }, [contentType]);

  /**
   * Start editing existing item
   */
  const startEdit = useCallback((item) => {
    setIsCreating(false);
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  }, []);

  /**
   * Cancel edit/create
   */
  const cancelEdit = useCallback(() => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
    setIsCreating(false);
  }, []);

  /**
   * Save item (create or update)
   */
  const save = useCallback(async (dataOverride = null) => {
    try {
      const dataToSave = dataOverride || formData;
      
      if (isCreating) {
        // Create new item
        const response = await api.post('/content-items', dataToSave);
        const newItem = response.data.data;
        setItems(prev => [newItem, ...prev]);
        showToast('Content created successfully', 'success');
      } else {
        // Update existing item
        const response = await api.put(`/content-items/${editingItem._id}`, dataToSave);
        const updatedItem = response.data.data;
        setItems(prev => prev.map(item => 
          item._id === editingItem._id ? updatedItem : item
        ));
        showToast('Content updated successfully', 'success');
      }
      
      cancelEdit();
      return true;
    } catch (error) {
      console.error('Failed to save:', error);
      showToast(
        error.response?.data?.error || 'Failed to save content', 
        'error'
      );
      return false;
    }
  }, [isCreating, formData, editingItem, cancelEdit, showToast]);

  /**
   * Delete item
   */
  const deleteItem = useCallback(async (item) => {
    try {
      await api.delete(`/content-items/${item._id}`);
      setItems(prev => prev.filter(i => i._id !== item._id));
      showToast('Content deleted successfully', 'success');
      setConfirmOpen(false);
      setItemToDelete(null);
      return true;
    } catch (error) {
      console.error('Failed to delete:', error);
      showToast(
        error.response?.data?.error || 'Failed to delete content', 
        'error'
      );
      return false;
    }
  }, [showToast]);

  /**
   * Show delete confirmation
   */
  const confirmDelete = useCallback((item) => {
    setItemToDelete(item);
    setConfirmOpen(true);
  }, []);

  /**
   * Cancel delete confirmation
   */
  const cancelDelete = useCallback(() => {
    setConfirmOpen(false);
    setItemToDelete(null);
  }, []);

  /**
   * Update form data
   */
  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Reorder items
   */
  const reorderItems = useCallback(async (reorderedItems) => {
    try {
      const updates = reorderedItems.map((item, index) => ({
        id: item._id,
        order: index
      }));
      
      await api.patch('/content-items/reorder', { items: updates });
      setItems(reorderedItems);
      showToast('Order updated successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to reorder:', error);
      showToast('Failed to update order', 'error');
      return false;
    }
  }, [showToast]);

  /**
   * Create item (alias for save when creating)
   */
  const createItem = useCallback(async (data) => {
    try {
      const dataToSave = { contentType, ...data };
      const response = await api.post('/content-items', dataToSave);
      const newItem = response.data.data;
      setItems(prev => [newItem, ...prev]);
      showToast('Content created successfully', 'success');
      return { success: true, data: newItem };
    } catch (error) {
      console.error('Failed to create:', error);
      showToast(
        error.response?.data?.error || 'Failed to create content', 
        'error'
      );
      return { success: false, error };
    }
  }, [contentType, showToast]);

  /**
   * Update item (alias for save when updating)
   */
  const updateItem = useCallback(async (itemId, data) => {
    try {
      const dataToSave = { contentType, ...data };
      const response = await api.put(`/content-items/${itemId}`, dataToSave);
      const updatedItem = response.data.data;
      setItems(prev => prev.map(item => 
        item._id === itemId ? updatedItem : item
      ));
      showToast('Content updated successfully', 'success');
      return { success: true, data: updatedItem };
    } catch (error) {
      console.error('Failed to update:', error);
      showToast(
        error.response?.data?.error || 'Failed to update content', 
        'error'
      );
      return { success: false, error };
    }
  }, [contentType, showToast]);

  return {
    // Data
    items,
    loading,
    error,
    
    // Modal state
    showModal,
    isCreating,
    editingItem,
    formData,
    
    // Confirmation state
    confirmOpen,
    itemToDelete,
    
    // Actions
    fetchItems,
    startCreate,
    startEdit,
    cancelEdit,
    save,
    createItem,    // Alias for creating
    updateItem,    // Alias for updating
    deleteItem,
    confirmDelete,
    cancelDelete,
    updateFormData,
    setFormData,
    reorderItems
  };
};

export default useEditableContent;
